import {createCroppedImageBitmap, generateMipLevel} from "#browser-utils/image-bitmap"
import type {Vector2Like} from "@cm/math"
import {RetiledImageBase, RetiledImageTile} from "@cm/utils"

export class RetiledImage extends RetiledImageBase<ImageBitmap> {
    protected async extractSubTileFromRemoteTile(
        tileData: ImageBitmap,
        tileIndex: Vector2Like,
        remoteMipLevelCorrectionScale: number,
    ): Promise<RetiledImageTile<ImageBitmap>> {
        const remoteTileMargin = this.tiledImage.imageTilesDescriptor.tileMargin

        const subTileX = tileIndex.x % (this.atlasTilesPerRemoteTile.x / remoteMipLevelCorrectionScale)
        const subTileY = tileIndex.y % (this.atlasTilesPerRemoteTile.y / remoteMipLevelCorrectionScale)

        const sx = remoteTileMargin + (subTileX * this.tileSize.width - this.tileMargin) * remoteMipLevelCorrectionScale
        const sy = remoteTileMargin + (subTileY * this.tileSize.height - this.tileMargin) * remoteMipLevelCorrectionScale
        const sw = Math.min(
            this.tileSizeWithMargin.width * remoteMipLevelCorrectionScale,
            tileData.width - (remoteTileMargin - this.tileMargin * remoteMipLevelCorrectionScale) - sx,
        )
        const sh = Math.min(
            this.tileSizeWithMargin.height * remoteMipLevelCorrectionScale,
            tileData.height - (remoteTileMargin - this.tileMargin * remoteMipLevelCorrectionScale) - sy,
        )

        if (sw <= 0 || sh <= 0) {
            throw new Error("ThreeTiledTexture: calculated sub-tile region is empty")
        }

        if (remoteMipLevelCorrectionScale === 1) {
            // Common path: reference the cache-held source bitmap with a crop rect — no new ImageBitmap allocated
            return new RetiledImageTile(tileData, {
                x: sx,
                y: sy,
                width: sw,
                height: sh,
            })
        }

        // scale > 1: must downscale progressively — unavoidable bitmap creation
        let currImage = await createCroppedImageBitmap(tileData, sx, sy, sw, sh)
        while (remoteMipLevelCorrectionScale > 1) {
            remoteMipLevelCorrectionScale >>= 1
            const nextImage = await generateMipLevel(currImage)
            currImage.close()
            currImage = nextImage
        }
        return new RetiledImageTile(
            currImage,
            {
                x: 0,
                y: 0,
                width: currImage.width,
                height: currImage.height,
            },
            () => currImage.close(),
        )
    }
}
